/* eslint-disable indent */
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { Form, Formik } from 'formik';

import { Input, SingleSelect } from '@/common';
import { ColorPicker, Label, LABEL_TYPE, Spinner, ToggleSwitch } from '@/components/generic';
import { TOGGLE_THEMES } from '@/components/generic/ToggleSwitch/toggle-switch-config';
import { PAGE_SIZE, TRIBUTE_VIDEO } from '@/constants';
import { AuthContext } from '@/contexts';
import {
  CustomAttributeType,
  ORGANIZATION_PERMISSION_TYPE,
  PERMISSION_TYPE,
  TributeVideoBackgroundType
} from '@/enums';
import { RemoveSpecialCharacters } from '@/helpers/NamingHelper';
import { dynamicValidationSchema } from '@/pages/Event/components/SetupValidation';
import { LeftChevronIcon } from '@/resources';
import {
  useGenerateCustomAttributesTributeVideoQuery,
  useGetCustomAttributeTemplatesByEventType,
  useGetEventTags,
  useGetEventTributeVideos,
  useGetMovementBundles,
  useGetOrganizationByIdSimplifiedQuery
} from '@/services';
import { TestIds } from '@/shared/test-ids';
import {
  Asset,
  CustomAttributeTemplate,
  CustomAttributeValue,
  HexColor,
  OrganizationPermission,
  SelectOptionProps,
  Tag,
  TributeVideoDataType,
  TributeVideoOptionsType
} from '@/types';

type SetupProps = {
  eventTypeId: number;
  setHasMusicStep: (hasMusicStep: boolean) => void;
  setEnableNext: React.Dispatch<React.SetStateAction<boolean>>;
  setTributeVideoName: React.Dispatch<React.SetStateAction<string>>;
  setCustomAttributeValues: React.Dispatch<React.SetStateAction<CustomAttributeValue[]>>;
  setMovementBundleId: React.Dispatch<React.SetStateAction<number | null>>;
  setTributeVideoOptions: React.Dispatch<React.SetStateAction<TributeVideoOptionsType>>;
  tributeVideoData: TributeVideoDataType;
  organizationId?: number;
};

export type InitialValues = {
  [key: string]: string | number | SelectOptionProps | null;
};

export const Setup = ({
  eventTypeId,
  setHasMusicStep,
  setEnableNext,
  setTributeVideoName,
  setCustomAttributeValues,
  setMovementBundleId,
  setTributeVideoOptions,
  tributeVideoData,
  organizationId
}: SetupProps) => {
  const [t] = useTranslation('common');
  const { eventId } = useParams();
  const [textToggle, setTextToggle] = useState(tributeVideoData?.options?.textsEnabled ?? false);
  const [musicToggle, setMusicToggle] = useState(tributeVideoData?.options?.musicEnabled ?? true);
  const [barsToggle, setBarsToggle] = useState(
    tributeVideoData?.options?.imageBordersEnabled ?? true
  );
  const [introToggle, setIntroToggle] = useState(tributeVideoData?.options?.introEnabled ?? true);
  const [outroToggle, setOutroToggle] = useState(tributeVideoData?.options?.outroEnabled ?? true);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [barsColor, setBarsColor] = useState(
    tributeVideoData?.options?.barsColor ? '#' + tributeVideoData?.options?.barsColor : '#000000'
  );
  const { permissions } = useContext(AuthContext);
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const [durationOfSingleImage, setDurationOfSingleImage] = useState(
    tributeVideoData?.options?.durationOfSingleImage ?? 0
  );
  const [movementId, setMovementId] = useState<number | undefined>(
    tributeVideoData?.movementBundleId ?? undefined
  );
  const [temporaryTributeVideoName, setTemporaryTributeVideoName] = useState(
    tributeVideoData?.name ?? undefined
  );

  const { data: allTags } = useGetEventTags(eventId as string);

  const tributeVideoTags = allTags?.data
    ?.filter((tag: Tag) => tag.name === TRIBUTE_VIDEO)
    .map((tag: Tag) => `${tag.id}`);

  const { data: allAssets } = useGetEventTributeVideos(
    eventId as string,
    PAGE_SIZE,
    hasOrgAccess ? [] : tributeVideoTags?.length === 0 ? ['-1'] : tributeVideoTags
  );

  const tributeVideos = allAssets?.pages.flatMap((page) =>
    page.data?.items.filter((item: Asset & { name: string }) =>
      ['Tribute Video', 'Diaporama', 'Huldevideo'].some((substring) =>
        item.name?.includes(substring)
      )
    )
  );
  const lastTributeVideoName = tributeVideos?.[tributeVideos?.length - 1]?.name;
  const numberInTributeVideoName = lastTributeVideoName?.match(/\d+/g)?.pop() ?? 0;

  const { data: customAttributeTemplates, isLoading: isLoadingCustomAttributeTemplates } =
    useGetCustomAttributeTemplatesByEventType(eventTypeId, CustomAttributeType.TRIBUTE_VIDEO);

  const { data: movementBundles, isLoading: isLoadingBundles } = useGetMovementBundles();

  const { data: organization } = useGetOrganizationByIdSimplifiedQuery(Number(organizationId));
  const hasCustomVideoResolutionPermission = (
    organization?.data?.paymentPlan?.organizationPermissions ?? []
  ).some(
    (permission: OrganizationPermission) =>
      permission.name === ORGANIZATION_PERMISSION_TYPE.CUSTOM_VIDEO_RESOLUTION
  );
  const customResolution: string | null =
    organization?.data?.organizationSettings?.customResolution ?? null;

  const { data: generatedCustomAttributes, isLoading: isLoadingGeneratedCustomAttributes } =
    useGenerateCustomAttributesTributeVideoQuery(Number(eventId), !!eventTypeId);

  const getTextInputTestIdByLabelKey = (labelKey: string) => {
    switch (labelKey) {
      case 'title':
        return TestIds.tributeVideo.titleInput;
      case 'nameOfDeceased':
      case 'nameOfTheDeceased':
        return TestIds.tributeVideo.nameOfDeceasedInput;
      case 'birthDeath':
      case 'birthAndDeath':
        return TestIds.tributeVideo.birthDeathInput;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (tributeVideoData?.options?.musicEnabled !== undefined) {
      setHasMusicStep(tributeVideoData?.options?.musicEnabled);
    }
  }, [tributeVideoData]);

  const getBarsOptions = () => {
    return [
      {
        label: t('useThemeColor'),
        value: null
      },
      {
        label: t('customColor'),
        value: barsColor.replace(/^#/, '').slice(0, 6),
        icon: (
          <div
            className='flex size-3 rounded-full'
            style={{ backgroundColor: barsColor }}
            onClick={() => setIsPickerOpen(true)}
          />
        )
      }
    ];
  };

  const getStyleOptions = () => {
    return movementBundles?.data.map((bundle: { id: number; name: string }) => {
      return {
        label: t(bundle.name),
        value: bundle.id
      };
    });
  };

  const getBackgroundTypeOptions = () => {
    return [
      { label: t('motionBackground'), value: TributeVideoBackgroundType.Motion },
      { label: t('stillBackground'), value: TributeVideoBackgroundType.Still },
      { label: t('contentBlurBackground'), value: TributeVideoBackgroundType.ContentBased },
      { label: t('blackBackground'), value: TributeVideoBackgroundType.Black }
    ];
  };

  const getResolutionOptions = () => [
    { label: t('default'), value: null },
    ...(customResolution ? [{ label: customResolution, value: customResolution }] : [])
  ];

  const getInitialFormValues = (): InitialValues => {
    const barsOption = tributeVideoData?.options?.barsColor
      ? getBarsOptions()[1]
      : getBarsOptions()[0];
    const styleOption =
      movementId === undefined
        ? tributeVideoData?.movementBundleId
          ? getStyleOptions()?.find(
              (option: SelectOptionProps) => option.value === tributeVideoData?.movementBundleId
            )
          : getStyleOptions()?.[0]
        : getStyleOptions()?.find((option: SelectOptionProps) => option.value === movementId);
    const backgroundTypeKey = tributeVideoData?.options
      ?.backgroundType as unknown as keyof typeof TributeVideoBackgroundType;
    const backgroundTypeOption =
      getBackgroundTypeOptions().find(
        (option) => option.value === TributeVideoBackgroundType[backgroundTypeKey]
      ) ?? getBackgroundTypeOptions()[0];
    const resolutionOption =
      getResolutionOptions().find(
        (option) => option.value === tributeVideoData?.options?.resolution
      ) ?? getResolutionOptions()[0];

    const initialValues: InitialValues = {
      bars: barsOption,
      style: styleOption,
      backgroundType: backgroundTypeOption,
      resolution: resolutionOption,
      duration: tributeVideoData?.options?.durationOfSingleImage
        ? tributeVideoData?.options?.durationOfSingleImage
        : durationOfSingleImage > 5
          ? durationOfSingleImage
          : 5,
      name:
        temporaryTributeVideoName ??
        tributeVideoData?.name ??
        t('tributeVideoWithNumber', { number: (Number(numberInTributeVideoName) ?? 0) + 1 })
    };

    if (generatedCustomAttributes?.data && textToggle) {
      (tributeVideoData?.customAttributeValues.length > 0
        ? tributeVideoData.customAttributeValues
        : generatedCustomAttributes.data
      ).forEach((attribute: { customAttributeTemplateId: number; value: string }) => {
        initialValues[attribute.customAttributeTemplateId] = attribute.value ?? '';
      });
    }

    return initialValues;
  };

  const initialValues = useMemo(
    () => getInitialFormValues(),
    [
      tributeVideoData,
      generatedCustomAttributes,
      textToggle,
      movementBundles?.data,
      customResolution
    ]
  );

  return isLoadingBundles ? (
    <Spinner />
  ) : (
    <Formik
      enableReinitialize
      onSubmit={() => undefined}
      initialValues={initialValues}
      validationSchema={dynamicValidationSchema(
        customAttributeTemplates?.data,
        textToggle,
        musicToggle
      )}>
      {({ errors, touched, setFieldValue, values, isValid, validateForm }) => {
        useEffect(() => {
          if (isValid) {
            setEnableNext(true);
          } else {
            setEnableNext(false);
          }
        }, [isValid, values, musicToggle]);

        useEffect(() => {
          setTributeVideoName(RemoveSpecialCharacters(values.name as string));
          setMovementBundleId(((values.style as SelectOptionProps)?.value as number) ?? null);
          setTributeVideoOptions({
            textsEnabled: textToggle,
            imageBordersEnabled: barsToggle,
            introEnabled: introToggle,
            outroEnabled: outroToggle,
            musicEnabled: musicToggle,
            barsColor: ((values.bars as SelectOptionProps)?.value as string) ?? null,
            durationOfSingleImage: values.duration && !musicToggle ? Number(values.duration) : 0,
            backgroundType: (values.backgroundType as SelectOptionProps)
              ?.value as TributeVideoBackgroundType,
            resolution: ((values.resolution as SelectOptionProps)?.value as string) ?? null
          });
          textToggle &&
            setCustomAttributeValues(
              customAttributeTemplates?.data.map((customAttribute: CustomAttributeTemplate) => {
                const matchingAttributeValue = tributeVideoData?.customAttributeValues.find(
                  (attr) => attr.customAttributeTemplateId === customAttribute.id
                );
                return {
                  id: matchingAttributeValue?.id,
                  customAttributeTemplateId: customAttribute.id,
                  value: values[customAttribute.id],
                  eventId: Number(eventId),
                  tributeVideoId: matchingAttributeValue?.tributeVideoId
                };
              }) ?? []
            );
          !textToggle && setCustomAttributeValues(tributeVideoData?.customAttributeValues);
          setDurationOfSingleImage(Number(values.duration));
        }, [values, musicToggle, textToggle, barsToggle, introToggle, outroToggle, barsColor]);

        return (
          <Form id='createEditTributeVideo'>
            <div className='flex w-full flex-col justify-between gap-6 md:flex-row md:gap-20'>
              <div className='flex w-full flex-col gap-4 md:w-1/2'>
                <Label
                  text={t('videoDetails')}
                  type={LABEL_TYPE.H3_BOLD}
                  className='text-primary-blue'
                />
                <Input
                  type='text'
                  id='name'
                  label={t('name')}
                  isRequired
                  errorMessage={errors.name}
                  isInvalid={!!errors.name && touched.name}
                  value={
                    typeof values.name === 'string' ? values.name : (values.name?.toString() ?? '')
                  }
                  handleChange={(text) => {
                    setFieldValue('name', text);
                    setTemporaryTributeVideoName(text);
                  }}
                />
                <SingleSelect
                  isClearable={false}
                  id='style'
                  label={t('videoStyle')}
                  placeholder={t('videoStyle')}
                  options={getStyleOptions()}
                  handleChange={(value: SingleValue<SelectOptionProps>) => {
                    setFieldValue('style', value);
                    setMovementId(Number(value?.value));
                  }}
                  value={values.style as SelectOptionProps}
                  errorMessage={errors.style}
                  isInvalid={!!errors.style && touched.style}
                  testId={TestIds.tributeVideo.videoStyleSelect}
                />
                <div className='flex flex-row justify-between md:mb-12'>
                  <Label text={t('enableTextInVideo')} type={LABEL_TYPE.PARAGRAPH2} />
                  <ToggleSwitch
                    handleToggle={() => {
                      setTextToggle(!textToggle);
                    }}
                    toggled={textToggle}
                    variant={TOGGLE_THEMES.LIGHT}
                    data-testid={TestIds.tributeVideo.enableTextInVideoToggle}
                  />
                </div>
              </div>
              <div className='flex w-full flex-col gap-6 md:w-1/2'>
                {textToggle &&
                  !isLoadingCustomAttributeTemplates &&
                  !isLoadingGeneratedCustomAttributes && (
                    <div className='flex flex-col gap-4'>
                      <Label
                        text={t('personalDetails')}
                        type={LABEL_TYPE.H3_BOLD}
                        className='text-primary-blue'
                      />
                      {customAttributeTemplates?.data.map(
                        (customAttribute: CustomAttributeTemplate) => (
                          <Input
                            type='text'
                            key={customAttribute.id}
                            id={customAttribute.id.toString()}
                            label={t(customAttribute.labelKey)}
                            dataTestId={getTextInputTestIdByLabelKey(customAttribute.labelKey)}
                            errorMessage={errors[customAttribute.id]}
                            isInvalid={!!errors[customAttribute.id]}
                            value={values[customAttribute.id] as string}
                            handleChange={(text) => {
                              setFieldValue(customAttribute.id.toString(), text);
                            }}
                          />
                        )
                      )}
                    </div>
                  )}
              </div>
            </div>
            <div className='flex w-full flex-col justify-between gap-6'>
              <div
                className='mt-6 flex cursor-pointer flex-row items-center gap-2 md:mt-0'
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
                <LeftChevronIcon
                  className={`size-3 ${showAdvancedOptions ? 'rotate-90' : '-rotate-90'} text-primary-blue`}
                />
                <Label
                  text={t('moreOptions')}
                  type={LABEL_TYPE.H3_BOLD}
                  className='text-primary-blue'
                />
              </div>
              {showAdvancedOptions && (
                <div className='flex w-full flex-col justify-between gap-4 md:flex-row md:gap-20'>
                  <div className='flex w-full flex-col gap-4 md:w-1/2'>
                    <>
                      <div className='flex flex-row justify-between'>
                        <Label text={t('enableIntro')} type={LABEL_TYPE.PARAGRAPH2} />
                        <ToggleSwitch
                          handleToggle={() => {
                            setIntroToggle(!introToggle);
                          }}
                          toggled={introToggle}
                          variant={TOGGLE_THEMES.LIGHT}
                          data-testid={TestIds.tributeVideo.enableIntroToggle}
                        />
                      </div>
                      <div className='flex flex-row justify-between'>
                        <Label text={t('enableOutro')} type={LABEL_TYPE.PARAGRAPH2} />
                        <ToggleSwitch
                          handleToggle={() => {
                            setOutroToggle(!outroToggle);
                          }}
                          toggled={outroToggle}
                          variant={TOGGLE_THEMES.LIGHT}
                          data-testid={TestIds.tributeVideo.enableOutroToggle}
                        />
                      </div>

                      <div className='flex flex-row justify-between'>
                        <Label text={t('enableBarsOnImages')} type={LABEL_TYPE.PARAGRAPH2} />
                        <ToggleSwitch
                          handleToggle={() => {
                            setBarsToggle(!barsToggle);
                          }}
                          toggled={barsToggle}
                          variant={TOGGLE_THEMES.LIGHT}
                          data-testid={TestIds.tributeVideo.enableBarsToggle}
                        />
                      </div>
                      {barsToggle && (
                        <div className='flex justify-between'>
                          <SingleSelect
                            isClearable={false}
                            id='bars'
                            label={t('barsColor')}
                            isRequired
                            options={getBarsOptions()}
                            handleChange={(value: SingleValue<SelectOptionProps>) => {
                              setFieldValue('bars', value as SelectOptionProps);
                              if (value?.value === 'chooseColor') {
                                setIsPickerOpen(true);
                              }
                            }}
                            value={values.bars as SelectOptionProps}
                            errorMessage={errors.bars}
                            isInvalid={!!errors.bars && touched.bars}
                          />
                        </div>
                      )}
                      <div className='relative'>
                        {isPickerOpen && (
                          <div className='absolute bottom-4 left-2'>
                            <ColorPicker
                              isOpen={isPickerOpen}
                              setIsOpen={setIsPickerOpen}
                              color={barsColor as HexColor}
                              showColorPreview={false}
                              className='!border-none'
                              onChange={(value) => {
                                setBarsColor(value);
                                const newOption = {
                                  label: t('customColor'),
                                  value: value.replace(/^#/, '').slice(0, 6),
                                  icon: (
                                    <div
                                      className='flex size-3 rounded-full'
                                      style={{ backgroundColor: value }}
                                      onClick={() => setIsPickerOpen(true)}
                                    />
                                  )
                                };
                                setFieldValue('bars', newOption);
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {hasCustomVideoResolutionPermission && customResolution && (
                        <div className='flex justify-between'>
                          <SingleSelect
                            isClearable={false}
                            id='resolution'
                            label={t('videoResolution')}
                            options={getResolutionOptions()}
                            handleChange={(value: SingleValue<SelectOptionProps>) => {
                              setFieldValue('resolution', value as SelectOptionProps);
                            }}
                            value={values.resolution as SelectOptionProps}
                            testId={TestIds.tributeVideo.resolutionSelect}
                            containerClassName='w-48'
                          />
                        </div>
                      )}
                    </>
                  </div>
                  <div className='flex w-full flex-col gap-4 md:w-1/2'>
                    <div className='flex flex-row justify-between'>
                      <Label text={t('enableMusicInVideo')} type={LABEL_TYPE.PARAGRAPH2} />
                      <ToggleSwitch
                        handleToggle={() => {
                          setMusicToggle(!musicToggle);
                          setHasMusicStep(!musicToggle);
                          if (musicToggle) {
                            setFieldValue(
                              'duration',
                              durationOfSingleImage === 0 ? 5 : durationOfSingleImage
                            );
                          }
                          validateForm();
                        }}
                        toggled={musicToggle}
                        variant={TOGGLE_THEMES.LIGHT}
                        data-testid={TestIds.tributeVideo.enableMusicToggle}
                      />
                    </div>
                    {!musicToggle && (
                      <div className='flex flex-row justify-between'>
                        <Input
                          type='number'
                          id='duration'
                          label={t('durationOfSingleImage')}
                          isRequired={!musicToggle}
                          errorMessage={errors.duration}
                          isInvalid={!!errors.duration && touched.duration}
                          min={5}
                        />
                      </div>
                    )}
                    <SingleSelect
                      id={'backgroundType'}
                      label={t('backgroundType')}
                      options={getBackgroundTypeOptions()}
                      isClearable={false}
                      handleChange={(value: SingleValue<SelectOptionProps>) => {
                        setFieldValue('backgroundType', value);
                      }}
                      value={values.backgroundType as SelectOptionProps}
                    />
                  </div>
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
